<template>
  <!-- 260919 무료 현판(포맥스) 신청 바텀시트 — 통합 통계 S1 [무료로 받기]. 사업장 주소·연락처를 미리 채우고 사이즈만 고르게. -->
  <div class="modal-dim on">
    <div class="fr-sheet-wrap">
      <div class="fr-sheet modal">
        <div class="fr-sheet__handle" aria-hidden="true"></div>
        <p class="fr-sheet__title">무료 현판 신청</p>
        <p class="fr-sheet__biz">{{ data?.business?.name || '' }}</p>

        <p class="fr-sheet__label">현판 사이즈</p>
        <div class="fr-sheet__sizes" role="radiogroup">
          <button v-for="s in sizes" :key="s.code" type="button" role="radio" :aria-checked="form.sizeCode === s.code"
                  :class="['fr-sheet__size', { 'is-active': form.sizeCode === s.code }]" @click="form.sizeCode = s.code">
            <strong>{{ s.name }}</strong><span>{{ s.dim }}</span>
          </button>
        </div>

        <label class="fr-sheet__label" for="fr-phone">연락처</label>
        <input id="fr-phone" v-model.trim="form.contactPhone" class="fr-sheet__input" type="tel" inputmode="tel" maxlength="20" placeholder="010-0000-0000" />

        <label class="fr-sheet__label" for="fr-addr">받을 주소</label>
        <input id="fr-addr" v-model.trim="form.address" class="fr-sheet__input" type="text" maxlength="120" placeholder="현판을 받을 매장 주소" />

        <label class="fr-sheet__label" for="fr-memo">요청사항 (선택)</label>
        <input id="fr-memo" v-model.trim="form.memoText" class="fr-sheet__input" type="text" maxlength="200" placeholder="예: 오후 배송 부탁드립니다" />

        <p v-if="error" class="fr-sheet__error">{{ error }}</p>
        <button type="button" class="fr-sheet__submit" :disabled="busy" @click="submit">{{ busy ? '접수 중…' : '신청하기' }}</button>
        <button type="button" class="fr-sheet__close" @click="close">닫기</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue';
import store from '@/store';
import { showTextModal } from '@/common/utils/ModalState';

const props = defineProps(['data']);
const emits = defineEmits(['close']);

const sizes = [
  { code: 'SMALL', name: '소형', dim: '200×150' },
  { code: 'MEDIUM', name: '중형', dim: '300×200' },
  { code: 'LARGE', name: '대형', dim: '400×300' }
]; // 실치수는 대표 컨펌 전 초안(§8-2) — 코드(SMALL/MEDIUM/LARGE)만 BE 계약

const biz = props.data?.business || {};
const form = reactive({
  sizeCode: 'MEDIUM',
  contactPhone: biz.phone || store.getters['user/getUserData']?.phone || '',
  address: [biz.roadAddress || biz.address || '', biz.addressDetail || ''].filter(Boolean).join(' '),
  memoText: ''
});
const busy = ref(false);
const error = ref('');

// 200ms = dim 페이드 아웃 대기(하우스 모달 패턴)
const close = () => { setTimeout(() => emits('close'), 200); };

const submit = async () => {
  error.value = '';
  if (!form.contactPhone) { error.value = '연락처를 입력해주세요.'; return; }
  busy.value = true;
  const r = await store.dispatch('business/submitFranchiseRequest', {
    businessNo: Number(props.data.businessNo),
    sizeCode: form.sizeCode,
    bizName: biz.name || null,
    contactPhone: form.contactPhone,
    address: form.address || null,
    memoText: form.memoText || null
  });
  busy.value = false;
  if (!r.ok) { error.value = r.message; return; }
  close();
  showTextModal('신청이 접수되었습니다.\n현판이 도착하면 매장에 붙이고 간판온 카카오 채널로 사진을 보내주세요.', '확인', () => {
    if (typeof props.data?.onDone === 'function') props.data.onDone();
  });
};
</script>

<style scoped>
.fr-sheet-wrap { position: absolute; inset: 0; z-index: 1; display: flex; align-items: flex-end; justify-content: center; }
.fr-sheet.modal {
  --blue: #3182F6;
  --blue-dark: #306DF7;
  --ice: #EEF3FF;
  --wash: #F7F8FA;
  --ink: #1F212A;
  --body: #3D3F4D;
  --muted: #737687;
  --line: #E5E7EB;
  --navy: #0F1F3D;
  box-sizing: border-box;
  width: 100%; min-width: 0; max-width: 600px; max-height: 90%;
  overflow-y: auto; overscroll-behavior-y: contain;
  padding: 12px 20px calc(20px + env(safe-area-inset-bottom));
  border-radius: 16px 16px 0 0; background: #fff;
  box-shadow: 0 -8px 32px rgba(15, 31, 61, .12);
  color: var(--ink); font-family: 'Pretendard', system-ui, -apple-system, sans-serif;
  font-size: 16px; line-height: 1.5; letter-spacing: -0.02em; text-align: left;
  animation: sheetUp .25s ease;
}
.fr-sheet > * { flex-shrink: 0; }
@keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.fr-sheet__handle { width: 40px; height: 4px; margin: 0 auto 20px; border-radius: 2px; background: #CDD1DA; }
.fr-sheet__title { margin: 0 0 6px; color: var(--navy); font-size: 22px; font-weight: 800; line-height: 1.4; }
.fr-sheet__biz { margin: 0 0 8px; color: var(--body); font-size: 16px; line-height: 1.5; word-break: keep-all; overflow-wrap: anywhere; }
.fr-sheet__label { display: block; margin: 20px 0 8px; color: var(--ink); font-size: 16px; font-weight: 700; line-height: 1.5; }
.fr-sheet__sizes { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
.fr-sheet__size { box-sizing: border-box; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; width: auto; min-width: 0; min-height: 84px; padding: 12px 4px; border: 2px solid var(--line); border-radius: 16px; background: #fff; color: var(--body); font-family: inherit; line-height: 1.5; text-decoration: none; cursor: pointer; }
.fr-sheet__size strong { font-size: 18px; font-weight: 700; }
.fr-sheet__size span { color: var(--body); font-size: 16px; font-variant-numeric: tabular-nums; white-space: nowrap; letter-spacing: -0.04em; }
.fr-sheet__input { box-sizing: border-box; width: 100%; min-width: 0; min-height: 52px; padding: 12px 14px; border: 1px solid #CFD3DC; border-radius: 12px; background: var(--wash); color: var(--ink); font-family: inherit; font-size: 16px; line-height: 1.5; }
.fr-sheet__input::placeholder { color: var(--muted); opacity: 1; }
.fr-sheet__input:focus { border-color: var(--blue-dark); background: #fff; }
.fr-sheet__error { margin: 12px 0 0; padding: 12px; border-radius: 12px; background: #FFF3F3; color: #B33232; font-size: 16px; line-height: 1.5; }
.fr-sheet__submit, .fr-sheet__close { width: 100%; min-height: 52px; padding: 12px 16px; border: 0; border-radius: 16px; font-family: inherit; font-size: 17px; font-weight: 700; line-height: 1.5; text-decoration: none; cursor: pointer; }
.fr-sheet__submit { margin-top: 24px; background: var(--blue-dark); color: #fff; }
.fr-sheet__submit:disabled { background: #DDE6F8; color: #526582; cursor: wait; }
.fr-sheet__close { margin-top: 8px; background: var(--wash); color: var(--body); }
.fr-sheet__size:focus-visible, .fr-sheet__input:focus-visible, .fr-sheet__submit:focus-visible, .fr-sheet__close:focus-visible { outline: 3px solid var(--navy); outline-offset: 2px; }
.fr-sheet__size:active, .fr-sheet__submit:not(:disabled):active, .fr-sheet__close:active { filter: brightness(.95); }
@media (max-width: 374px) { .fr-sheet.modal { padding-right: 16px; padding-left: 16px; } }
@media (prefers-reduced-motion: reduce) { .fr-sheet.modal { animation: none; } }
.fr-sheet__size.is-active { border-color: var(--blue-dark); background: var(--ice); color: var(--blue-dark); box-shadow: 0 2px 8px rgba(48, 109, 247, .1); }
.fr-sheet__size.is-active span { color: var(--blue-dark); }
</style>
